import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertStatus, type AlertImage } from "@/alerts/types/machine-alert";
import { labelConfidencePercent } from "@/shared/utils/confidence";
import { Button } from "@/ui/components/ui/button";
import { Badge } from "@/ui/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Separator } from "@/ui/components/ui/separator";
import { useUpdateAlert, useGetAlertById } from "@/alerts/api/queries/machine-alerts.queries";
import { useGetVideoAround } from "@/machines/api/queries/video.queries";
import { formatDate, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { alertStatusLabel } from "@/machines/utils/alert-status-label.ts";
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  Play,
  Loader2
} from "lucide-react";
import { toast } from "react-hot-toast";
import { Progress } from "@/ui/components/ui/progress";
import { Video } from "@/machines/types/video";

interface AlertDetailModalProps {
  payload: {
    alertId: string;
  };
  onClose: () => void;
}

const AlertDetailModal: React.FC<AlertDetailModalProps> = ({ payload, onClose }) => {
  const { t } = useTranslation("machines");
  const { alertId } = payload;
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Fetch alert data by ID
  const { data: alertData, isLoading: alertLoading, error: alertError } = useGetAlertById(alertId);
  const alert = alertData?.data;

  console.log(alert)
  
  // Ensure alert.timestamp is a valid Date object
  const alertTime = React.useMemo(() => {
    if (!alert?.timestamp) return new Date();
    
    const parsed = new Date(alert.timestamp);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [alert?.timestamp]);
  
  const updateAlertMutation = useUpdateAlert();
  
  // Only fetch video if we have valid origin and timestamp
  const shouldFetchVideo = alert?.origin && alertTime && !isNaN(alertTime.getTime());
  
  const { data: videoData, isLoading: videoLoading, error: videoError } = useGetVideoAround({
    device_mxid: alert?.device_mxid || '',
    timestamp: alertTime,
    page: 1,
    itemsPerPage: 10,
  });

  const pickClosestVideo = (videos: Video[] | undefined, target: Date): Video | undefined => {
    if (!videos || videos.length === 0) return undefined;
    const targetMs = target.getTime();
    // Prefer videos that encompass the target time; else minimal absolute distance
    const scored = videos.map(v => {
      const start = new Date(v.start_time).getTime();
      const end = new Date(v.end_time).getTime();
      const inRange = targetMs >= start && targetMs <= end;
      const distance = inRange ? 0 : Math.min(Math.abs(targetMs - start), Math.abs(targetMs - end));
      return { v, distance };
    });
    scored.sort((a, b) => a.distance - b.distance);
    return scored[0]?.v;
  };

  const closestVideo = React.useMemo(() => pickClosestVideo(videoData?.data, alertTime), [videoData?.data, alertTime]);

  // Event images come with the alert detail itself.
  const eventImages = alert?.images ?? [];
  const isImagesLoading = alertLoading;

  // Show loading state if alert is still loading
  if (alertLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">{t("alertDetail.loading")}</span>
        </div>
      </div>
    );
  }

  // Show error state if alert failed to load
  if (alertError || !alert) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-2" />
            <p className="text-sm text-red-500">{t("alertDetail.loadFailed")}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {alertError?.message || t("alertDetail.notFound")}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Debug logging
  console.log('Alert Modal Debug:', {
    alertOrigin: alert.origin,
    alertTime: alertTime,
    shouldFetchVideo,
    isValidTime: !isNaN(alertTime.getTime())
  });

  const handleStatusUpdate = async (newStatus: AlertStatus) => {
    if (!alert.id) {
      toast.error(t("alertDetail.toast.idNotFound"));
      return;
    }

    setIsUpdating(true);
    try {
      await updateAlertMutation.mutateAsync({
        id: alert.id.toString(),
        data: { status: newStatus }
      });
      
      toast.success(t("alertDetail.toast.statusUpdated", { status: alertStatusLabel(newStatus, t).toLowerCase() }));
      onClose();
    } catch (error) {
      toast.error(t("alertDetail.toast.updateFailed"));
      console.error("Error updating alert:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleShowOnTimeline = () => {
    // TODO: Navigate to timeline view with the alert timestamp
    toast.success(t("alertDetail.toast.navigatingToTimeline"));
    onClose();
  };

  const getStatusColor = (status: AlertStatus) => {
    switch (status) {
      case AlertStatus.Pending:
        return "bg-yellow-500";
      case AlertStatus.Resolved:
        return "bg-green-500";
      case AlertStatus.Ignored:
        return "bg-gray-500";
      default:
        return "bg-red-500";
    }
  };

  const getStatusIcon = (status: AlertStatus) => {
    switch (status) {
      case AlertStatus.Pending:
        return <AlertTriangle className="h-4 w-4" />;
      case AlertStatus.Resolved:
        return <CheckCircle className="h-4 w-4" />;
      case AlertStatus.Ignored:
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      <div className="flex-shrink-0 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${getStatusColor(alert.status)}`}>
              {getStatusIcon(alert.status)}
            </div>
            <div>
              <h2 className="text-xl font-semibold">{alert.type}</h2>
              <p className="text-sm text-muted-foreground">{alert.origin}</p>
            </div>
          </div>
          <Badge variant={alert.status === AlertStatus.Pending ? "destructive" : "secondary"}>
            {alertStatusLabel(alert.status, t)}
          </Badge>
        </div>
        <Separator className="mt-4" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto space-y-6 pr-2">
        {/* Alert Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t("alertDetail.info")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{formatDate(alertTime, "PPPp")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{formatDate(alertTime, "HH:mm:ss")}</span>
              </div>

              {/* Images slider (like in timeline) */}
              <div className="mt-4">
                {isImagesLoading ? (
                  <div className="flex items-center justify-center h-40 bg-muted rounded-lg">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      <div className="w-32"><Progress value={60} className="h-1" /></div>
                    </div>
                  </div>
                ) : eventImages.length === 0 ? (
                  // Nothing at all rather than an empty grey panel: an alert with
                  // no frames has nothing to show, and a box saying so takes the
                  // space the rest of the detail could use.
                  null
                ) : (
                  <div className="w-full max-w-md mx-auto p-0">
                    <div className="relative overflow-hidden rounded-lg">
                      <AlertImagesSlider images={eventImages} />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">{t("alertDetail.videoTitle")}</CardTitle>
          </CardHeader>
                  <CardContent>
          {videoLoading ? (
            <div className="flex items-center justify-center h-48 bg-muted rounded-lg">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="ml-2">{t("alertDetail.videoLoading")}</span>
            </div>
          ) : videoError ? (
            <div className="flex items-center justify-center h-48 bg-muted rounded-lg">
              <div className="text-center">
                <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-2" />
                <p className="text-sm text-red-500">{t("alertDetail.videoError")}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {videoError.message || t("alertDetail.unknownError")}
                </p>
              </div>
            </div>
          ) : closestVideo ? (
            <div className="aspect-video bg-black rounded-lg overflow-hidden max-h-[200px]">
              <video
                controls
                className="w-full h-full"
                src={closestVideo.signedUrl}
              >
                {t("alertDetail.videoUnsupported")}
              </video>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 bg-muted rounded-lg">
              <div className="text-center">
                <Play className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">{t("alertDetail.videoUnavailable")}</p>
                {!shouldFetchVideo && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("alertDetail.videoMissingParams")}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
        </Card>
        </div>

        {/* Video Section */}
        
      </div>

      {/* Fixed Footer */}
      <div className="flex-shrink-0 pt-4 border-t mt-4" >
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleShowOnTimeline}
            disabled={isUpdating}
          >
            <Clock className="h-4 w-4 mr-2" />
            {t("alertDetail.showOnTimeline")}
          </Button>
          
          {alert.status === AlertStatus.Pending && (
            <>
              <Button
                variant="outline"
                onClick={() => handleStatusUpdate(AlertStatus.Ignored)}
                disabled={isUpdating}
              >
                <XCircle className="h-4 w-4 mr-2" />
                {t("alertDetail.markAsFalse")}
              </Button>
              
              <Button
                onClick={() => handleStatusUpdate(AlertStatus.Resolved)}
                disabled={isUpdating}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {t("alertStatus.resolved")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface AlertImagesSliderProps {
  images: AlertImage[];
}

const AlertImagesSlider: React.FC<AlertImagesSliderProps> = ({ images }) => {
  const { t } = useTranslation("machines");
  // Default to the alert frame — the highest-confidence image, like MapEventCard.
  const alertFrameIndex = React.useMemo(() => {
    let best = 0;
    for (let i = 1; i < images.length; i++) {
      if ((labelConfidencePercent(images[i].labels) ?? -1) > (labelConfidencePercent(images[best].labels) ?? -1)) best = i;
    }
    return best;
  }, [images]);
  const [currentIndex, setCurrentIndex] = React.useState(alertFrameIndex);

  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);

  React.useEffect(() => {
    setCurrentIndex(alertFrameIndex);
  }, [images, alertFrameIndex]);

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="w-full max-h-[250px]">
      <div
        className="flex transition-transform duration-300 ease-in-out items-center"
        style={{
          width: `${images.length * 100}%`,
          transform: `translateX(-${currentIndex * (100 / images.length)}%)`,
          maxHeight: "250px",
        }}
      >
        {images.map((im, index) => (
          <div key={im.image_id} className="flex-shrink-0 w-full flex items-center justify-center" style={{ width: `${100 / images.length}%`, maxHeight: "250px" }}>
            <img
              src={im.signed_url}
              alt={t("alertDetail.imageAlt", { index: index + 1 })}
              className="object-contain"
              style={{ maxHeight: "250px", maxWidth: "100%" }}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-muted-foreground">
          {t("alertDetail.imageCounter", { index: currentIndex + 1, total: images.length })}
          {labelConfidencePercent(images[currentIndex]?.labels) != null && (
            <> · {t("alertDetail.imageConfidence", { value: formatNumber(labelConfidencePercent(images[currentIndex]?.labels)!) })}</>
          )}
        </span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={prevImage}>
            ‹
          </Button>
          <Button size="sm" variant="outline" onClick={nextImage}>
            ›
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlertDetailModal;
